import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SwUpdate } from '@angular/service-worker';
import { Subject, Observable } from 'rxjs';

export interface PwaInstallPromptEvent {
  showPrompt: boolean;
}

export interface PwaUpdateEvent {
  available: boolean;
  currentVersion?: any;
  availableVersion?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private promptEvent: any;
  private isInstallable = false;
  private readonly INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
  private readonly INSTALL_DISMISSED_TIMESTAMP_KEY = 'pwa-install-dismissed-timestamp';
  private readonly DAYS_TO_SHOW_AGAIN = 30; // Volver a mostrar después de 30 días
  private readonly DELAY_TO_SHOW_PROMPT = 5000; // 5 segundos

  // Observables para comunicar eventos
  private installPromptSubject = new Subject<PwaInstallPromptEvent>();
  private updateAvailableSubject = new Subject<PwaUpdateEvent>();

  public installPrompt$: Observable<PwaInstallPromptEvent> = this.installPromptSubject.asObservable();
  public updateAvailable$: Observable<PwaUpdateEvent> = this.updateAvailableSubject.asObservable();

  constructor(private swUpdate: SwUpdate) {
    this.initPwaPrompt();
    this.initServiceWorkerUpdates();
  }

  /**
   * Inicializa el manejo de eventos de instalación PWA
   */
  private initPwaPrompt() {
    // Solo habilitar el prompt en plataforma web (no en app nativa)
    if (Capacitor.getPlatform() === 'web') {
      window.addEventListener('beforeinstallprompt', (event: any) => {
        // Prevenir que el navegador muestre su propio prompt
        event.preventDefault();
        // Guardar el evento para usarlo después
        this.promptEvent = event;
        this.isInstallable = true;
        console.log('PWA instalable detectada');

        // Verificar si se debe mostrar el prompt
        this.scheduleInstallPrompt();
      });

      window.addEventListener('appinstalled', () => {
        console.log('PWA instalada exitosamente');
        this.promptEvent = null;
        this.isInstallable = false;
        // Limpiar localStorage
        localStorage.removeItem(this.INSTALL_DISMISSED_KEY);
        localStorage.removeItem(this.INSTALL_DISMISSED_TIMESTAMP_KEY);
      });
    }
  }

  /**
   * Inicializa el manejo de actualizaciones del Service Worker
   */
  private initServiceWorkerUpdates() {
    console.log('[PWA Service] SwUpdate isEnabled:', this.swUpdate.isEnabled);
    console.log('[PWA Service] Platform:', Capacitor.getPlatform());

    if (this.swUpdate.isEnabled && Capacitor.getPlatform() === 'web') {
      console.log('[PWA Service] Inicializando detección de actualizaciones...');

      // Mostrar información de la versión actual
      this.logCurrentVersion();

      // 1. Verificar al inicio
      this.checkForUpdates();

      // 2. Verificar cuando el usuario vuelve a la pestaña/app
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          console.log('[PWA Service] Pestaña visible, verificando actualizaciones...');
          this.checkForUpdates();
        }
      });

      // 3. Suscribirse a todos los eventos de actualización
      this.swUpdate.versionUpdates.subscribe(event => {
        console.log('[PWA Service] Evento de versión recibido:', event);

        if (event.type === 'VERSION_DETECTED') {
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('🔍 [PWA] NUEVA VERSIÓN DETECTADA - Descargando...');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('🔑 Hash de nueva versión:', event.version.hash);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        } else if (event.type === 'VERSION_READY') {
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('✅ [PWA] NUEVA VERSIÓN LISTA PARA ACTIVAR!');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('📦 Versión actual:', event.currentVersion);
          console.log('🔑 Hash actual:', event.currentVersion.hash);
          console.log('───────────────────────────────────────────────────────────');
          console.log('🆕 Nueva versión:', event.latestVersion);
          console.log('🔑 Hash nuevo:', event.latestVersion.hash);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

          // Notificar que hay actualización disponible
          this.updateAvailableSubject.next({
            available: true,
            currentVersion: event.currentVersion,
            availableVersion: event.latestVersion
          });
        } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.error('❌ [PWA] ERROR AL INSTALAR NUEVA VERSIÓN');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.error('Error:', event.error);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        }
      });

      // 4. Manejo de errores irrecuperables
      this.swUpdate.unrecoverable.subscribe(event => {
        console.error('[PWA Service] Estado irrecuperable del Service Worker:', event.reason);
        // Recargar la página automáticamente
        document.location.reload();
      });
    } else {
      console.warn('[PWA Service] Service Worker no habilitado o no está en plataforma web');
    }
  }

  /**
   * Programa la visualización del prompt de instalación
   */
  private scheduleInstallPrompt() {
    // Verificar si ya está instalada
    if (this.isAppInstalled()) {
      console.log('PWA ya está instalada');
      return;
    }

    // Verificar si el usuario ya rechazó el prompt y cuándo
    if (this.shouldSkipPrompt()) {
      return;
    }

    // Esperar unos segundos antes de mostrar el prompt para no ser intrusivo
    setTimeout(() => {
      if (this.canInstall()) {
        this.installPromptSubject.next({ showPrompt: true });
      }
    }, this.DELAY_TO_SHOW_PROMPT);
  }

  /**
   * Verifica si se debe omitir el prompt basado en rechazos anteriores
   */
  private shouldSkipPrompt(): boolean {
    const dismissed = localStorage.getItem(this.INSTALL_DISMISSED_KEY);
    const dismissedTimestamp = localStorage.getItem(this.INSTALL_DISMISSED_TIMESTAMP_KEY);

    if (!dismissed || !dismissedTimestamp) {
      return false;
    }

    const daysSinceDismissed = this.getDaysSince(parseInt(dismissedTimestamp));

    if (daysSinceDismissed >= this.DAYS_TO_SHOW_AGAIN) {
      // Ha pasado suficiente tiempo, volver a mostrar
      console.log(`Han pasado ${daysSinceDismissed} días desde el último rechazo. Mostrando prompt de nuevo.`);
      return false;
    }

    console.log(`Usuario rechazó la instalación hace ${daysSinceDismissed} días. Esperando ${this.DAYS_TO_SHOW_AGAIN - daysSinceDismissed} días más.`);
    return true;
  }

  /**
   * Calcula los días transcurridos desde un timestamp
   */
  private getDaysSince(timestamp: number): number {
    const now = Date.now();
    const diff = now - timestamp;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  /**
   * Verifica si la app puede ser instalada
   */
  canInstall(): boolean {
    return this.isInstallable && this.promptEvent !== null;
  }

  /**
   * Muestra el prompt de instalación del navegador
   */
  async promptInstall(): Promise<boolean> {
    if (!this.promptEvent) {
      console.warn('No hay prompt de instalación disponible');
      return false;
    }

    try {
      // Mostrar el prompt nativo del navegador
      this.promptEvent.prompt();

      // Esperar la respuesta del usuario
      const choiceResult = await this.promptEvent.userChoice;

      if (choiceResult.outcome === 'accepted') {
        console.log('Usuario aceptó instalar la PWA');
        this.isInstallable = false;
        this.promptEvent = null;
        return true;
      } else {
        console.log('Usuario rechazó instalar la PWA');
        return false;
      }
    } catch (error) {
      console.error('Error al mostrar el prompt de instalación:', error);
      return false;
    }
  }

  /**
   * Marca el prompt como rechazado por el usuario
   */
  dismissInstallPrompt() {
    localStorage.setItem(this.INSTALL_DISMISSED_KEY, 'true');
    localStorage.setItem(this.INSTALL_DISMISSED_TIMESTAMP_KEY, Date.now().toString());
    console.log('Prompt de instalación marcado como rechazado');
  }

  /**
   * Verifica si la app ya está instalada
   */
  isAppInstalled(): boolean {
    // Detectar si se ejecuta en modo standalone (ya instalada)
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true ||
           document.referrer.includes('android-app://');
  }

  /**
   * Verifica si hay actualizaciones disponibles del Service Worker
   */
  checkForUpdates() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().catch(err => {
        console.error('Error al verificar actualizaciones:', err);
      });
    }
  }

  /**
   * Activa la actualización y recarga la página
   */
  async activateUpdate(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
      console.log('Activando actualización y recargando...');
      document.location.reload();
    } catch (err) {
      console.error('Error al activar la actualización:', err);
      // Intentar recargar de todas formas si falla la activación
      document.location.reload();
    }
  }

  /**
   * Obtiene información sobre si está en modo web
   */
  isWebPlatform(): boolean {
    return Capacitor.getPlatform() === 'web';
  }

  /**
   * Muestra en consola la versión actual instalada
   */
  private async logCurrentVersion() {
    try {
      // Obtener la versión actual desde el Service Worker
      const registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        console.log('[PWA Service] No hay Service Worker registrado');
        return;
      }

      // Leer el ngsw.json para obtener el hash de la versión
      const response = await fetch('/ngsw.json');
      if (response.ok) {
        const ngsw = await response.json();
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📦 [PWA] VERSIÓN INSTALADA:');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🔑 Hash:', ngsw.configVersion);
        console.log('📅 Timestamp:', new Date(ngsw.timestamp));
        console.log('📄 Index:', ngsw.index);

        if (ngsw.appData) {
          console.log('📱 App Data:', ngsw.appData);
        }

        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      }
    } catch (error) {
      console.warn('[PWA Service] No se pudo obtener información de la versión:', error);
    }
  }

  /**
   * Obtiene la versión actual instalada (método público para debugging)
   */
  async getCurrentVersion(): Promise<any> {
    try {
      const response = await fetch('/ngsw.json');
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('[PWA Service] Error obteniendo versión:', error);
    }
    return null;
  }
}
